import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Clock, RefreshCw, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  under_review: "bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]",
  suspended: "bg-slate-100 text-slate-600 border-slate-200",
};

const APPROVABLE = ["pending", "under_review"];

export default function AdminSenderIDs() {
  const [senderIds, setSenderIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [rejectModal, setRejectModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [assignForm, setAssignForm] = useState({ user_email: "", sender_id: "", business_name: "" });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { loadSenderIds(); }, [filter]);

  const loadSenderIds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (filter) params.append("status", filter);
      const { data } = await API.get(`/sender-ids/admin/all?${params}`);
      setSenderIds(data.sender_ids || []);
    } catch { toast.error("Failed to load sender IDs"); }
    finally { setLoading(false); }
  };

  const approve = async (id) => {
    try {
      await API.put(`/sender-ids/admin/${id}/approve`);
      toast.success("Sender ID approved");
      loadSenderIds();
    } catch { toast.error("Failed to approve"); }
  };

  const reject = async () => {
    if (!selected) return;
    try {
      await API.put(`/sender-ids/admin/${selected._id}/reject`, { reason: rejectReason });
      toast.success("Sender ID rejected");
      setRejectModal(false);
      loadSenderIds();
    } catch { toast.error("Failed to reject"); }
  };

  const assignSenderId = async () => {
    if (!assignForm.user_email || !assignForm.sender_id) {
      toast.error("Customer email and Sender ID are required");
      return;
    }
    setAssigning(true);
    try {
      await API.post("/sender-ids/admin/create", assignForm);
      toast.success(`Sender ID ${assignForm.sender_id.toUpperCase()} assigned & approved`);
      setAssignModal(false);
      setAssignForm({ user_email: "", sender_id: "", business_name: "" });
      loadSenderIds();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign sender ID");
    } finally { setAssigning(false); }
  };

  const pending = senderIds.filter((s) => s.status === "pending").length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Sender IDs</h1>
          <p className="text-sm text-slate-500 mt-1">{pending} pending review</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setAssignModal(true)} size="sm" className="bg-[#0c1e41] hover:bg-[#091629] h-9" data-testid="assign-sender-id-btn">
            <Plus size={14} className="mr-2" /> Assign to Customer
          </Button>
          <Button onClick={loadSenderIds} variant="outline" size="sm" className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {["", "pending", "under_review", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${filter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3.5 font-semibold">Sender ID</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Customer</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">Business</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden lg:table-cell">Purpose</th>
                <th className="text-left px-5 py-3.5 font-semibold">Status</th>
                <th className="text-left px-5 py-3.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={6}><div className="h-10 bg-slate-100 animate-pulse mx-5 my-2 rounded" /></td></tr>)
              : senderIds.length === 0 ? <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400 text-sm">No sender IDs found</td></tr>
              : senderIds.map((s) => (
                <tr key={s._id} className="table-row-hover">
                  <td className="px-5 py-3">
                    <span className="font-mono text-sm font-bold text-[#0c1e41] bg-[#eef1f8] border border-[#c0cfe8] px-2 py-1 rounded">{s.sender_id}</span>
                  </td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    <div className="text-sm text-slate-900 font-medium">{s.user_name}</div>
                    <div className="text-xs text-slate-500">{s.user_email}</div>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-sm text-slate-700">{s.business_name}</td>
                  <td className="px-5 py-3 hidden lg:table-cell text-sm text-slate-500 max-w-40 truncate">{s.purpose}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className={`text-xs capitalize ${STATUS_COLORS[s.status] || ""}`}>{s.status?.replace("_", " ")}</Badge>
                  </td>
                  <td className="px-5 py-3">
                    {APPROVABLE.includes(s.status) && (
                      <div className="flex gap-2">
                        <button data-testid={ADMIN.approveSenderIdButton} onClick={() => approve(s._id)} className="text-xs text-emerald-700 font-semibold px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded transition-colors">Approve</button>
                        <button onClick={() => { setSelected(s); setRejectReason(""); setRejectModal(true); }} className="text-xs text-red-700 font-semibold px-2 py-1 bg-red-50 hover:bg-red-100 border border-red-200 rounded transition-colors">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      <Dialog open={rejectModal} onOpenChange={setRejectModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Reject Sender ID — {selected?.sender_id}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Rejection Reason</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Does not meet requirements" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
            </div>
            <Button onClick={reject} className="w-full bg-red-600 hover:bg-red-700">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Sender ID Modal */}
      <Dialog open={assignModal} onOpenChange={setAssignModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Assign Sender ID to Customer</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Directly create and approve a sender ID for any customer</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Customer Email</Label>
              <Input
                value={assignForm.user_email}
                onChange={(e) => setAssignForm({ ...assignForm, user_email: e.target.value })}
                placeholder="customer@example.com"
                className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400"
                data-testid="assign-customer-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Sender ID <span className="text-slate-400 font-normal">(max 11 chars)</span></Label>
              <Input
                value={assignForm.sender_id}
                onChange={(e) => setAssignForm({ ...assignForm, sender_id: e.target.value.toUpperCase().slice(0, 11) })}
                placeholder="MYBRAND"
                className="bg-white border-slate-300 text-slate-900 h-10 font-mono placeholder:text-slate-400"
                data-testid="assign-sender-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Business Name <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                value={assignForm.business_name}
                onChange={(e) => setAssignForm({ ...assignForm, business_name: e.target.value })}
                placeholder="Company Ltd"
                className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400"
              />
            </div>
            <Button onClick={assignSenderId} disabled={assigning} className="w-full bg-[#0c1e41] hover:bg-[#091629]" data-testid="confirm-assign-sender-id">
              {assigning ? "Assigning..." : "Assign & Approve"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
